import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Provider, atom, createStore } from 'jotai'
// import { DevTools } from 'jotai-devtools'
import { queryClientAtom } from 'jotai-tanstack-query'
import React from 'react'
import { ApplyBeforeNavigates } from '~/components/modal/Provider'
import type { EnvType } from '~/env'
import { useConstant } from '~/hooks/useConstant'
import type { CookieMap } from '~/lib/cookie'
import { AtomDevTools } from '~/lib/jotai'
import { useRemixLoading } from '~/lib/loading'
import type { Device } from '~/lib/ua'
import { device$ } from '~/lib/ua'
import { InitNavigation } from '~/navigation'
import { cookie$ } from '~/stores/cookie'
import { env$ } from '~/stores/env'
import { InitNow } from '~/stores/now'

// import 'jotai-devtools/styles.css'

type Props = {
  env: EnvType
  device: Device
  cookie: CookieMap
}

export function Providers({ children, ...props }: React.PropsWithChildren<Props>) {
  // https://tanstack.com/query/latest/docs/framework/react/guides/ssr
  // queryClient が複数のリクエストで共有されないように
  const queryClient = useConstant(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          // With SSR, we usually want to set some default staleTime
          // above 0 to avoid refetching immediately on the client
          staleTime: 60 * 1000,
        },
      },
    })
  })
  return (
    <QueryClientProvider client={queryClient}>
      <JotaiProvider queryClient={queryClient} {...props}>
        <Init />
        {children}
      </JotaiProvider>
    </QueryClientProvider>
  )
}

function Init() {
  useRemixLoading()
  return null
}

/** atomWithError 専用なので、他の用途で利用しないこと */
// atomWithError でエラーをセットするためのエスケープハッチ
// store 自身に store を保持しておくことで、Getter だけでも store 経由で atom をセットできるようにする
export const store$ = atom<ReturnType<typeof createStore>>(null as any)

function JotaiProvider({
  children,
  queryClient,
  env,
  device,
  cookie,
}: React.PropsWithChildren<Props & { queryClient: QueryClient }>) {
  const store = useConstant(() => {
    const store = createStore()
    // 最初から queryClient, now は値があるので、ここで値を入れないといけない。
    // useEffect で store の値を入れ替えると queryClient が
    // TanStack Query の初期値から変わったタイミングで再度クエリが叩かれてしまうため。
    // useConstant で初期化してしまって大丈夫なのは props の値が変わることがないから。
    store.set(queryClientAtom, queryClient)
    store.set(env$, env)
    store.set(device$, device)
    store.set(cookie$, cookie)
    // @ts-ignore
    store.set(store$, store)
    return store
  })

  return (
    <Provider store={store}>
      <AtomDevTools store={store} />
      <InitNavigation />
      <ApplyBeforeNavigates />
      <InitNow />
      {/*<DevTools store={store} />*/}
      {children}
    </Provider>
  )
}
